import {
  APIComponentAttributes,
  APIEntityType,
  ArdoqId,
  ProvenanceSource,
} from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import {
  getAddChildComponentMenuItem,
  getComponentNavigateToMenuItem,
  getCreateReferenceMenuItem,
  getDeleteComponentMenuItems,
  getDragToCreateReferenceMenuItem,
  getDropdownDivider,
  getEditPropertiesMenuItem,
  getEditStyleMenuItem,
  getExcludeComponentMenuItem,
  getFilterMenuItem,
  getHistoryMenuItem,
  getTraversalMenuItems,
  getMergeEntitiesMenuItem,
  getToggleLockMenuItem,
  seeDetailsDrawer,
  getTraversalInferenceMenuItems,
  getLinkToLucidChartMenuItem,
  getNewDatasetSubmenu,
} from './menuItems';
import {
  DropdownItem,
  DropdownOptionType,
  DropdownSubmenu,
} from '@ardoq/dropdown-menu';
import { isTruthy } from '@ardoq/common-helpers';
import { TrackedContextMenus, getTrackingFunction } from './tracking';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { getActiveScenarioId } from 'streams/activeScenario/activeScenario$';
import { componentInterface } from '@ardoq/component-interface';
import { checkHasWriteAccess } from './utils';
import { logError } from '@ardoq/logging';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { Features, hasScenarioFeature } from '@ardoq/features';
import { getScenarioContextComponentContextMenu } from './scenarioContextComponentContextMenu';
import { PermissionContext } from '@ardoq/access-control';
import { type GetScenarioContextMenuOptions } from 'scope/scenarioMenuOptions';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { focusedChromeNow } from 'scope/focusedChromeNow';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { canComponentHaveChildren } from './canComponentHaveChildren';
import { getParentComponent } from './getParentComponent';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';

type ReadOnlyComponentMenuArgs = {
  components: APIComponentAttributes[];
  workspaceAndModel: WorkspaceAndModelLookup;
  parent: APIComponentAttributes | null;
  isViewpointMode: boolean;
  allowsContextNavigation: boolean;
  menuComponentTrackingFunction: (optionTitle: string) => void;
  i18nLocale: I18nLocale;
};

const readOnlyComponentMenu = ({
  components,
  workspaceAndModel,
  parent,
  isViewpointMode,
  allowsContextNavigation,
  menuComponentTrackingFunction,
  i18nLocale,
}: ReadOnlyComponentMenuArgs) => {
  const componentIds = toIds(components);
  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);
  if (isCanvasPrototype && isViewpointMode) {
    return [
      ...getTraversalInferenceMenuItems(componentIds, i18nLocale),
      seeDetailsDrawer(
        components,
        isCanvasPrototype,
        isViewpointMode,
        i18nLocale
      ),
      getNewDatasetSubmenu(
        componentIds,
        menuComponentTrackingFunction,
        i18nLocale
      ),
      getHistoryMenuItem(
        componentIds[0],
        APIEntityType.COMPONENT,
        menuComponentTrackingFunction,
        i18nLocale
      ),
    ].filter(isTruthy);
  }
  const menu = isViewpointMode
    ? [
        ...getTraversalInferenceMenuItems(componentIds, i18nLocale),
        seeDetailsDrawer(
          components,
          isCanvasPrototype,
          isViewpointMode,
          i18nLocale
        ),
        ...getTraversalMenuItems(
          componentIds,
          menuComponentTrackingFunction,
          i18nLocale
        ),
      ]
    : [
        ...getTraversalInferenceMenuItems(componentIds, i18nLocale),
        ...(allowsContextNavigation
          ? [
              getComponentNavigateToMenuItem({
                component: components[0],
                parent,
                trackingFn: menuComponentTrackingFunction,
                i18nLocale,
              }),
              getDropdownDivider(),
            ]
          : []),
        getFilterMenuItem({
          target: { component: components[0], workspaceAndModel },
          trackingFunction: menuComponentTrackingFunction,
          i18nLocale,
          isViewpointMode,
        }),
      ];

  return menu.length ? menu : null;
};

type GetComponentMenuArgs = {
  components: APIComponentAttributes[];
  eventTargetModelId?: string | null;
  isViewpointMode: boolean;
  isStartInNavigator?: boolean;
  getNextTreeSelectionCandidate?: () => ArdoqId | null;
  permissionContext: PermissionContext;
  getScenarioContextMenuOptions: GetScenarioContextMenuOptions;
  workspaceAndModel: WorkspaceAndModelLookup;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  referenceCreation: ReferenceCreationMenuState;
  i18nLocale: I18nLocale;
  provenanceSource?: ProvenanceSource;
} & GetContextMenuOptionsArguments;
export const getComponentMenu = ({
  components,
  eventTargetModelId,
  event,
  target,
  sourceNode,
  x,
  y,
  isViewpointMode,
  isStartInNavigator,
  getNextTreeSelectionCandidate = () => null,
  permissionContext,
  getScenarioContextMenuOptions,
  workspaceAndModel,
  componentsById,
  referenceCreation,
  i18nLocale,
  provenanceSource,
}: GetComponentMenuArgs): DropdownItem[] | null => {
  const componentIds = toIds(components);
  if (isViewpointMode && isDiscoverJourney()) {
    return null;
  }
  if (
    eventTargetModelId &&
    !components.some(c => c._id === eventTargetModelId)
  ) {
    logError(
      new Error('Invalid target model for component context menu'),
      'Component context menu is called on something that is not a component.'
    );
    return null;
  }

  const allowsContextNavigation =
    focusedChromeOperations.allowsContextNavigation(focusedChromeNow());

  const isSingleComponent = componentIds.length === 1;
  const canHaveChildren =
    isSingleComponent &&
    canComponentHaveChildren(components[0], workspaceAndModel);
  const parent = isSingleComponent
    ? getParentComponent(components[0], componentsById)
    : null;

  const isScenarioContext = componentIds.some(
    componentInterface.isScenarioContextComponent
  );

  if (isScenarioContext) {
    return getScenarioContextComponentContextMenu({
      components,
      workspaceAndModel,
      target,
      isViewpointMode,
      allowsContextNavigation,
      canHaveChildren,
      parent,
      permissionContext,
      i18nLocale,
      provenanceSource,
    });
  }

  const menuComponentTrackingFunction = getTrackingFunction(
    target,
    TrackedContextMenus.COMPONENT_CONTEXT_MENU
  );
  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);
  const scenarioId = getActiveScenarioId();
  const workspaceIds = [...new Set(components.map(c => c.rootWorkspace))];
  const hasWriteAccess = checkHasWriteAccess({
    workspaceIds,
    permissionContext,
    components,
    referenceSourceComponentsById: {},
  });
  const componentTypeIds = components.map(c => c.typeId);
  const isComponentsSameType = new Set(componentTypeIds).size === 1;

  if (!hasWriteAccess && !isSingleComponent) {
    if (isCanvasPrototype) {
      const submenu = getNewDatasetSubmenu(
        componentIds,
        menuComponentTrackingFunction,
        i18nLocale
      );
      return isComponentsSameType && submenu ? [submenu] : [];
    }
    // no context menu for multiselect with no write access except for viewpoint features
    return getTraversalMenuItems(
      componentIds,
      menuComponentTrackingFunction,
      i18nLocale
    );
  }

  if (!hasWriteAccess) {
    return readOnlyComponentMenu({
      components,
      workspaceAndModel,
      parent,
      isViewpointMode,
      allowsContextNavigation,
      menuComponentTrackingFunction,
      i18nLocale,
    });
  }

  const hasLockedComponents = lockOperations.isAnyLocked(
    components.map(c => c.lock),
    getFeatureContext()
  );

  // "Add new dataset with AI"
  const addNewDatasetWithAiItem = getTraversalInferenceMenuItems(
    componentIds,
    i18nLocale
  );

  // "See component details"
  const detailsDrawerItem = seeDetailsDrawer(
    components,
    isCanvasPrototype,
    isViewpointMode,
    i18nLocale
  );

  // "Create reference"
  const createReferenceMenuItem = getCreateReferenceMenuItem(
    componentIds,
    event,
    x,
    y,
    menuComponentTrackingFunction,
    i18nLocale,
    isViewpointMode,
    eventTargetModelId ?? undefined,
    isStartInNavigator
  );

  // "Draw reference"
  const dragToCreateReferenceMenuItem = getDragToCreateReferenceMenuItem({
    componentIds,
    x,
    y,
    target,
    sourceNode,
    isCreatingReference: referenceCreation.isCreatingReference,
    trackingFn: menuComponentTrackingFunction,
    i18nLocale,
  });

  // "Exclude component"
  const excludeComponentMenuItem = getExcludeComponentMenuItem(
    components,
    i18nLocale
  );

  // "Open new dataset from selection", "Apply a saved viewpoint",
  const traversalMenuItems = getTraversalMenuItems(
    componentIds,
    menuComponentTrackingFunction,
    i18nLocale
  );

  // "Delete component", "Exclude components ..."
  const deleteMenuItems = getDeleteComponentMenuItems(
    components,
    menuComponentTrackingFunction,
    getNextTreeSelectionCandidate,
    isCanvasPrototype,
    i18nLocale,
    provenanceSource
  );

  // "View history"
  const historyItem =
    isSingleComponent && hasWriteAccess
      ? getHistoryMenuItem(
          componentIds[0],
          APIEntityType.COMPONENT,
          menuComponentTrackingFunction,
          i18nLocale
        )
      : null;

  // "Lock", "Unlock"
  const toggleLockItem = getToggleLockMenuItem({
    components,
    references: [],
    trackingFn: menuComponentTrackingFunction,
    i18nLocale,
    permissionContext,
  });

  // "Add selection to scenario", "Create scenario from selection"
  const scenarioOptions = getScenarioContextMenuOptions(componentIds);
  const scenarioMenuItems = scenarioOptions.length
    ? [...scenarioOptions, getDropdownDivider()]
    : [];

  // "Edit properties", "Edit selected components", "Edit selected references"
  const editPropertiesMenuItem =
    !hasLockedComponents &&
    getEditPropertiesMenuItem(
      componentIds,
      [],
      menuComponentTrackingFunction,
      i18nLocale,
      isViewpointMode
    );

  // "Edit style"
  const editStyleMenuItem = getEditStyleMenuItem(
    componentIds,
    [],
    menuComponentTrackingFunction,
    i18nLocale,
    isViewpointMode
  );

  // "Add child component"
  const addChildComponentMenuItem =
    !hasLockedComponents &&
    isSingleComponent &&
    getAddChildComponentMenuItem({
      component: components[0],
      canHaveChildren,
      trackingFn: menuComponentTrackingFunction,
      isViewpointMode,
      i18nLocale,
      provenanceSource,
    });

  // "Scenarios"
  const scenariosMenuDropDown: DropdownSubmenu | null = scenarioMenuItems.length
    ? {
        name: 'Scenarios',
        label: t(i18nLocale, 'contextMenus:scenarios', 'Scenarios'),
        options: scenarioMenuItems,
        type: DropdownOptionType.SUBMENU,
        leftIcon: {
          name: IconName.SCENARIO,
        },
      }
    : null;

  // "New dataset"
  const newDatasetSubmenu = getNewDatasetSubmenu(
    componentIds,
    menuComponentTrackingFunction,
    i18nLocale
  );

  if (isViewpointMode) {
    if (isCanvasPrototype) {
      const menuItemsForSingleComponent = isSingleComponent
        ? [
            editPropertiesMenuItem,
            createReferenceMenuItem,
            dragToCreateReferenceMenuItem,
            addChildComponentMenuItem,
            scenarioMenuItems.length && getDropdownDivider(),
          ]
        : [];
      const menuItemsForMultipleComponents = isSingleComponent
        ? []
        : [createReferenceMenuItem];

      const menuForCanvasPrototype = [
        ...addNewDatasetWithAiItem,
        excludeComponentMenuItem,
        ...menuItemsForSingleComponent,
        ...menuItemsForMultipleComponents,
        scenariosMenuDropDown,
        isComponentsSameType && newDatasetSubmenu,
        historyItem && getDropdownDivider(),
        historyItem,
        ...deleteMenuItems,
      ];
      return menuForCanvasPrototype.length > 0
        ? menuForCanvasPrototype.filter(isTruthy)
        : null;
    }

    const menu = (
      hasLockedComponents
        ? [
            ...addNewDatasetWithAiItem,
            excludeComponentMenuItem,
            detailsDrawerItem,
            historyItem,
            toggleLockItem,
            (historyItem || toggleLockItem) && getDropdownDivider(),
            createReferenceMenuItem,
            scenarioMenuItems.length && getDropdownDivider(),
            ...scenarioMenuItems,
            ...traversalMenuItems,
          ]
        : [
            ...addNewDatasetWithAiItem,
            excludeComponentMenuItem,
            detailsDrawerItem,
            editPropertiesMenuItem,
            editStyleMenuItem,
            createReferenceMenuItem,
            isSingleComponent && addChildComponentMenuItem,
            ...deleteMenuItems,
            historyItem && getDropdownDivider(),
            historyItem,
            toggleLockItem,
            scenarioMenuItems.length && getDropdownDivider(),
            ...scenarioMenuItems,
            ...traversalMenuItems,
            getDropdownDivider(),
          ]
    ).filter(isTruthy);
    return menu.length > 0 ? menu : null;
  }
  const menu = [
    ...(hasLockedComponents
      ? [
          historyItem,
          toggleLockItem,
          getDropdownDivider(),

          ...(createReferenceMenuItem
            ? [createReferenceMenuItem, getDropdownDivider()]
            : []),
        ]
      : [
          editPropertiesMenuItem,
          editStyleMenuItem,
          createReferenceMenuItem,
          // AI generated scenarios will be available without the scenario feature enabled,
          // but won't allow adding components
          ...(!scenarioId || (scenarioId && hasScenarioFeature())
            ? [addChildComponentMenuItem]
            : []),
          ...(!scenarioId ? deleteMenuItems : []),
          getDropdownDivider(),

          ...(historyItem || toggleLockItem
            ? [historyItem, toggleLockItem, getDropdownDivider()]
            : []),
        ]),
    ...(scenarioId ? deleteMenuItems : scenarioMenuItems),
    ...getLinkToLucidChartMenuItem(componentIds[0], i18nLocale),
    ...(isSingleComponent
      ? [
          ...(allowsContextNavigation
            ? [
                getComponentNavigateToMenuItem({
                  component: components[0],
                  parent,
                  trackingFn: menuComponentTrackingFunction,
                  i18nLocale,
                }),
                getDropdownDivider(),
              ]
            : []),
          getFilterMenuItem({
            target: { component: components[0], workspaceAndModel },
            trackingFunction: menuComponentTrackingFunction,
            i18nLocale,
            isViewpointMode,
          }),
        ]
      : [
          !hasLockedComponents &&
            getMergeEntitiesMenuItem(
              componentIds,
              APIEntityType.COMPONENT,
              menuComponentTrackingFunction,
              i18nLocale
            ),
        ]),
  ].filter(isTruthy);

  return menu.length > 0 ? menu : null;
};
